import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Dropdown, MenuItem, Select } from '../components/ui';
import { cn } from '../lib/utils';
import type { ExportFormat } from './exportScene';

/** Farbgebung der Speicherstatus-Pille je Zustand. */
const STATUS_TONE: Record<SaveStatus, string> = {
  idle: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
  saving: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  saved: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200',
  error: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
  live: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200',
};

export type EditorTool = 'select' | 'connect' | 'comment';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'live';

interface ToolbarProps {
  tool: EditorTool;
  status: SaveStatus;
  savedAt: string | null;
  hasSelection: boolean;
  readOnly?: boolean;
  canUndo: boolean;
  canRedo: boolean;
  zoomPercent: number;
  isFullscreen: boolean;
  /** Globaler Linien-Brücken-Schalter (Baustein D): true = Bögen an Kreuzungen an. */
  lineBridges: boolean;
  onSetTool: (tool: EditorTool) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomFit: () => void;
  onToggleFullscreen: () => void;
  onToggleLineBridges: () => void;
  onExport: (format: ExportFormat) => void;
  onOpenHelp: () => void;
}

function Divider() {
  return <span className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-600" />;
}

export function Toolbar({
  tool,
  status,
  savedAt,
  hasSelection,
  readOnly = false,
  canUndo,
  canRedo,
  zoomPercent,
  isFullscreen,
  lineBridges,
  onSetTool,
  onDelete,
  onDuplicate,
  onUndo,
  onRedo,
  onSave,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onZoomFit,
  onToggleFullscreen,
  onToggleLineBridges,
  onExport,
  onOpenHelp,
}: ToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-white/70 p-2 [&_button]:touch-manipulation dark:border-slate-700 dark:bg-slate-800/70">
      <Link
        to="/"
        title={t('drawings.backToOverview')}
        aria-label={t('drawings.backToOverview')}
        className="inline-flex min-h-[2.25rem] items-center justify-center rounded-md px-2.5 py-2 text-base leading-none transition-colors hover:bg-slate-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-slate-700/60"
      >
        ⌂
      </Link>
      <Divider />

      {/* Verbinden-Umschalter – die Navigation laeuft sonst gestenbasiert ueber die Maus */}
      <Button
        variant={tool === 'connect' ? 'primary' : 'secondary'}
        disabled={readOnly}
        onClick={() => onSetTool(tool === 'connect' ? 'select' : 'connect')}
      >
        {t('editor.connect')}
      </Button>
      {/* Kommentar-Werkzeug (Kollaboration Phase 5): Klick auf Objekt/Punkt setzt einen Pin */}
      <Button
        variant={tool === 'comment' ? 'primary' : 'secondary'}
        disabled={readOnly}
        onClick={() => onSetTool(tool === 'comment' ? 'select' : 'comment')}
      >
        {t('editor.comment')}
      </Button>
      <Divider />

      <Button
        variant="ghost"
        className="px-2"
        disabled={readOnly || !canUndo}
        onClick={onUndo}
        title={t('editor.undo')}
        aria-label={t('editor.undo')}
      >
        ↶
      </Button>
      <Button
        variant="ghost"
        className="px-2"
        disabled={readOnly || !canRedo}
        onClick={onRedo}
        title={t('editor.redo')}
        aria-label={t('editor.redo')}
      >
        ↷
      </Button>
      <Divider />
      <div className="flex items-center gap-1">
        <Button variant="ghost" className="px-2" title={t('editor.zoomOut')} aria-label={t('editor.zoomOut')} onClick={onZoomOut}>
          −
        </Button>
        <button
          type="button"
          className="min-h-[2.25rem] min-w-[3.5rem] rounded-md px-2 py-1 text-xs tabular-nums text-slate-600 hover:bg-slate-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-slate-300 dark:hover:bg-slate-700/60"
          title={t('editor.zoomReset')}
          aria-label={t('editor.zoomReset')}
          onClick={onZoomReset}
        >
          {zoomPercent}%
        </button>
        <Button variant="ghost" className="px-2" title={t('editor.zoomIn')} aria-label={t('editor.zoomIn')} onClick={onZoomIn}>
          +
        </Button>
        <Button variant="ghost" className="px-2" title={t('editor.zoomFit')} onClick={onZoomFit}>
          {t('editor.zoomFit')}
        </Button>
      </div>
      <Divider />

      {/* Mehr – Sekundäraktionen ins Overflow-Menü */}
      <Dropdown variant="ghost" label={`${t('editor.more')} ▾`}>
        {(close) => (
          <>
            <MenuItem disabled={readOnly || !hasSelection} onClick={() => { onDuplicate(); close(); }}>
              {t('editor.duplicate')}
            </MenuItem>
            <MenuItem danger disabled={readOnly || !hasSelection} onClick={() => { onDelete(); close(); }}>
              {t('editor.delete')}
            </MenuItem>
            <MenuItem disabled={readOnly} onClick={() => { onToggleLineBridges(); close(); }}>
              {lineBridges ? '✓ ' : ''}{t('editor.lineBridges.toggle')}
            </MenuItem>
            <MenuItem onClick={() => { onToggleFullscreen(); close(); }}>
              {isFullscreen ? t('editor.fullscreenExit') : t('editor.fullscreen')}
            </MenuItem>
            <MenuItem onClick={() => { onOpenHelp(); close(); }}>{t('editor.sc.title')}</MenuItem>
          </>
        )}
      </Dropdown>

      <div className="ml-auto flex items-center gap-2">
        <span
          className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium', STATUS_TONE[status])}
          title={status === 'saved' && savedAt ? t('editor.status.savedAt', { time: savedAt }) : t(`editor.status.${status}`)}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
          <span className="hidden sm:inline">
            {status === 'saved' && savedAt ? t('editor.status.savedAt', { time: savedAt }) : t(`editor.status.${status}`)}
          </span>
        </span>
        <Select
          className="py-1"
          aria-label={t('editor.export')}
          value=""
          onChange={(e) => {
            const value = e.target.value as ExportFormat | '';
            if (value) onExport(value);
          }}
        >
          <option value="">{t('editor.export')}</option>
          <option value="png">PNG</option>
          <option value="svg">SVG</option>
          <option value="pdf">PDF</option>
        </Select>
        <Button disabled={readOnly} onClick={onSave}>
          {t('editor.save')}
        </Button>
      </div>
    </div>
  );
}
